import "./styles.css";
import {
  applyMove,
  applyMoves,
  createSolvedState,
  isSolved,
} from "./cube/model.js";
import { inverseMove, makeMove, scrambleSequence } from "./cube/moves.js";
import { formatMove, formatSequence } from "./cube/notation.js";
import { makeRng } from "./cube/scramble.js";
import { validateState } from "./cube/validation.js";
import { TurnAnimator } from "./animation/turn-animator.js";
import { DragTurn } from "./animation/drag-turn.js";
import { CubeView } from "./rendering/cube-view.js";
import { CameraRig } from "./rendering/camera-rig.js";
import { estimateDeviceClass } from "./rendering/quality.js";
import { SolutionTimeline } from "./timeline/solution-timeline.js";

const $ = (selector) => document.querySelector(selector);
const cloneMove = (move) => ({ ...move, layers: [...move.layers] });
const MIN_PLAYBACK_SPEED = 0.5;
const MAX_PLAYBACK_SPEED = 10;
const MOVE_BUTTON_MIN_WIDTH = 32;
const MOVE_GRID_GAP = 5;
const MOVE_ROW_HEIGHT = 33;
const MOVE_WINDOW_BUFFER_ROWS = 4;
const MOVE_LIST_TOP_SPACE = 10;
const MOVE_LIST_BOTTOM_SPACE = 18;

const elements = {
  stage: $("#cube-stage"),
  mount: $("#canvas-mount"),
  loading: $("#loading-cover"),
  cubeSize: $("#cube-size"),
  cubeSizeDown: $("#decrease-cube-size"),
  cubeSizeUp: $("#increase-cube-size"),
  cubeName: $("#cube-name"),
  status: $("#stage-status"),
  statusText: $("#status-text"),
  instanceCount: $("#instance-count"),
  performanceToggle: $("#toggle-performance"),
  performanceHud: $("#performance-hud"),
  debugFps: $("#debug-fps"),
  debugFrame: $("#debug-frame"),
  debugCubies: $("#debug-cubies"),
  debugFacelets: $("#debug-facelets"),
  debugDraws: $("#debug-draws"),
  debugMemory: $("#debug-memory"),
  moveCount: $("#move-count"),
  seed: $("#seed-input"),
  scrambleLength: $("#scramble-length"),
  reset: $("#reset-button"),
  scramble: $("#scramble-button"),
  undo: $("#undo-button"),
  redo: $("#redo-button"),
  solve: $("#solve-button"),
  solverCapability: $("#solver-capability"),
  solveProgress: $("#solve-progress"),
  progressLabel: $("#progress-label"),
  progressValue: $("#progress-value"),
  progressBar: $("#progress-bar"),
  cancelSolve: $("#cancel-solve"),
  timelineCard: $("#timeline-card"),
  solutionTitle: $("#solution-title"),
  solutionDetail: $("#solution-detail"),
  solutionMoveCount: $("#solution-move-count"),
  solutionTime: $("#solution-time"),
  solutionCheck: $("#solution-check"),
  timer: $("#timer"),
  range: $("#timeline-range"),
  timelineEnd: $("#timeline-end"),
  start: $("#timeline-start"),
  prev: $("#timeline-prev"),
  play: $("#timeline-play"),
  next: $("#timeline-next"),
  end: $("#timeline-end-button"),
  speed: $("#playback-speed"),
  speedDown: $("#decrease-playback-speed"),
  speedUp: $("#increase-playback-speed"),
  speedValue: $("#playback-speed-value"),
  loop: $("#loop-playback"),
  moveStrip: $("#move-strip"),
  moveStripSpacer: $("#move-strip-spacer"),
  moveStripWindow: $("#move-strip-window"),
  guide: $("#notation-dialog"),
  openGuide: $("#open-guide"),
};

let N = 3;
let state = createSolvedState(N);
let history = [];
let redoStack = [];
let playbackSpeed = Number(readPreference("speed", "1")) || 1;
let timeline = null;
let timelineBaseHistory = [];
let timelineCursor = 0;
let renderedTimeline = null;
let renderedTimelineCursor = -1;
let timelineWindowStart = -1;
let timelineWindowEnd = -1;
let timelineWindowColumns = 1;
let timelineWindowFrame = 0;
let timelineWindowForceRender = false;
let timelineDomBuilds = 0;
let timelineWindowRenders = 0;
let playing = false;
let playbackGeneration = 0;
let activeCommit = null;
let solveController = null;
let solveGeneration = 0;
let solverModulePromise = null;
let timerStartedAt = 0;
let timerElapsed = 0;
let timerRunning = false;
let timerText = elements.timer.textContent;
let timerDomWrites = 0;
let dragPointer = null;
let sequenceRunning = false;
let sequenceGeneration = 0;

selectPlaybackSpeed(playbackSpeed);

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;
let view;
try {
  view = new CubeView(elements.mount, { deviceClass: estimateDeviceClass() });
} catch (error) {
  elements.loading.innerHTML = `<b>3D rendering is unavailable</b><small>${escapeHtml(error.message)}</small>`;
  setStatus("This browser could not start WebGL", "error");
  throw error;
}

const cameraRig = new CameraRig(view.camera, elements.stage);
cameraRig.fitCube(N, { immediate: true });
const animator = new TurnAnimator(view, {
  animate: true,
  reducedMotion,
  onCommit: commitAnimatedMove,
});
const dragTurn = new DragTurn(view, view.camera, {
  enabled: () =>
    !animator.isBusy && !solveController && !playing && !sequenceRunning,
  commitTurn: (move, fromAngle) => manualMove(move, { fromAngle }),
});

view.setState(state);
cameraRig.update();
view.render();
requestAnimationFrame(() => elements.loading.classList.add("done"));
installEvents();
updateAll();

const handleViewportResize = () => view.resize();
const resizeObserver = new ResizeObserver(handleViewportResize);
resizeObserver.observe(elements.mount);
window.addEventListener("resize", handleViewportResize, { passive: true });
const timelineResizeObserver = new ResizeObserver(() =>
  scheduleTimelineWindowRender(true),
);
timelineResizeObserver.observe(elements.moveStrip);

let frameCount = 0;
let frameWindowStarted = performance.now();
let lastFrame = frameWindowStarted;
let currentFps = 0;
let currentFrameMs = 0;

function renderLoop(now) {
  currentFrameMs = now - lastFrame;
  lastFrame = now;
  frameCount++;
  if (now - frameWindowStarted >= 500) {
    currentFps = (frameCount * 1000) / (now - frameWindowStarted);
    frameCount = 0;
    frameWindowStarted = now;
    updateDiagnostics();
  }
  cameraRig.update();
  animator.update(now);
  view.render();
  updateTimer(now);
  requestAnimationFrame(renderLoop);
}
requestAnimationFrame(renderLoop);

function installEvents() {
  elements.cubeSize.addEventListener("input", syncCubeSizeInputWidth);
  elements.cubeSize.addEventListener("change", () =>
    changeCubeSize(Number(elements.cubeSize.value)),
  );
  elements.cubeSizeDown.addEventListener("click", () => stepCubeSize(-1));
  elements.cubeSizeUp.addEventListener("click", () => stepCubeSize(1));
  elements.scramble.addEventListener("click", scrambleCube);
  elements.reset.addEventListener("click", resetCube);
  elements.undo.addEventListener("click", undoMove);
  elements.redo.addEventListener("click", redoMove);
  elements.solve.addEventListener("click", solveCube);
  elements.cancelSolve.addEventListener("click", cancelSolve);
  elements.performanceToggle.addEventListener("pointerdown", (event) =>
    event.stopPropagation(),
  );
  elements.performanceToggle.addEventListener("click", () => {
    const opening = elements.performanceHud.hidden;
    elements.performanceHud.hidden = !opening;
    elements.performanceToggle.setAttribute("aria-expanded", String(opening));
    elements.performanceToggle.classList.toggle("active", opening);
    if (opening) updateDiagnostics();
  });
  elements.speedDown.addEventListener("click", () => stepPlaybackSpeed(-1));
  elements.speedUp.addEventListener("click", () => stepPlaybackSpeed(1));

  $("#face-moves").addEventListener("click", (event) => {
    const button = event.target.closest("[data-move]");
    if (!button) return;
    const times = event.altKey ? 2 : event.shiftKey ? 3 : 1;
    manualMove(makeMove(N, { face: button.dataset.move, times }));
  });

  elements.openGuide.addEventListener("click", () =>
    elements.guide.showModal(),
  );

  elements.start.addEventListener("click", () => setTimelineCursor(0));
  elements.prev.addEventListener("click", previousTimelineMove);
  elements.play.addEventListener("click", togglePlayback);
  elements.next.addEventListener("click", () => nextTimelineMove(false));
  elements.end.addEventListener(
    "click",
    () => timeline && setTimelineCursor(timeline.length),
  );
  elements.range.addEventListener("input", () =>
    setTimelineCursor(Number(elements.range.value), { preservePlayback: true }),
  );
  elements.moveStrip.addEventListener("click", (event) => {
    const button = event.target.closest("[data-cursor]");
    if (button)
      setTimelineCursor(Number(button.dataset.cursor), {
        preservePlayback: true,
      });
  });
  elements.moveStrip.addEventListener(
    "scroll",
    () => scheduleTimelineWindowRender(),
    { passive: true },
  );

  // Layer turns capture pointer events before the orbit rig. A drag beginning
  // on a cubie turns it; a drag beginning on the backdrop reaches CameraRig.
  elements.stage.addEventListener(
    "pointerdown",
    (event) => {
      if (
        event.button !== 0 ||
        dragPointer !== null ||
        event.target.closest("button, .performance-hud")
      )
        return;
      const rect = elements.stage.getBoundingClientRect();
      const ndc = {
        x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
        y: -((event.clientY - rect.top) / rect.height) * 2 + 1,
      };
      if (dragTurn.down(ndc, { x: event.clientX, y: event.clientY })) {
        dragPointer = event.pointerId;
        cameraRig.enabled = false;
        elements.stage.setPointerCapture(event.pointerId);
        event.stopImmediatePropagation();
      }
    },
    true,
  );
  elements.stage.addEventListener(
    "pointermove",
    (event) => {
      if (event.pointerId !== dragPointer) return;
      dragTurn.move({ x: event.clientX, y: event.clientY });
      event.stopImmediatePropagation();
    },
    true,
  );
  const finishDrag = (event) => {
    if (event.pointerId !== dragPointer) return;
    dragTurn.up();
    dragPointer = null;
    cameraRig.enabled = true;
    event.stopImmediatePropagation();
  };
  elements.stage.addEventListener("pointerup", finishDrag, true);
  elements.stage.addEventListener(
    "pointercancel",
    (event) => {
      if (event.pointerId !== dragPointer) return;
      dragTurn.cancel();
      dragPointer = null;
      cameraRig.enabled = true;
      event.stopImmediatePropagation();
    },
    true,
  );

  document.addEventListener("keydown", handleKeyboard);
  window.addEventListener("beforeunload", () => {
    resizeObserver.disconnect();
    timelineResizeObserver.disconnect();
    window.removeEventListener("resize", handleViewportResize);
    view.dispose();
  });
}

function stepCubeSize(delta) {
  const current = Math.max(2, Math.trunc(Number(elements.cubeSize.value) || N));
  changeCubeSize(current + delta);
}

function handleKeyboard(event) {
  const tag = event.target?.tagName;
  if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
  const key = event.key.toUpperCase();
  if (["U", "D", "L", "R", "F", "B"].includes(key)) {
    event.preventDefault();
    manualMove(
      makeMove(N, {
        face: key,
        times: event.altKey ? 2 : event.shiftKey ? 3 : 1,
      }),
    );
  } else if (event.code === "Space" && timeline) {
    event.preventDefault();
    togglePlayback();
  } else if (event.key === "ArrowLeft" && timeline) {
    event.preventDefault();
    previousTimelineMove();
  } else if (event.key === "ArrowRight" && timeline) {
    event.preventDefault();
    nextTimelineMove(false);
  } else if (event.key === "?") {
    elements.guide.showModal();
  }
}

function changeCubeSize(rawN) {
  const nextN = Math.max(2, Math.trunc(rawN || 3));
  elements.cubeSize.value = String(nextN);
  syncCubeSizeInputWidth();
  if (nextN === N) return;
  const visibleItems = surfaceCubieCount(nextN) + 6 * nextN * nextN;
  if (
    visibleItems > 500_000 &&
    !window.confirm(
      `A ${nextN}×${nextN} cube needs about ${visibleItems.toLocaleString()} visible instances. Build it anyway?`,
    )
  ) {
    elements.cubeSize.value = String(N);
    syncCubeSizeInputWidth();
    return;
  }

  stopPlayback();
  cancelMoveSequence();
  cancelSolve();
  setStatus(`Building ${nextN}×${nextN}`, "busy");
  elements.loading.classList.remove("done");
  elements.loading.querySelector("b").textContent =
    `Building ${nextN}×${nextN}`;
  requestAnimationFrame(() => {
    try {
      N = nextN;
      view.buildCube(N);
      cameraRig.fitCube(N, { immediate: true });
      state = createSolvedState(N);
      history = [];
      redoStack = [];
      clearSolution();
      resetTimer();
      view.setState(state);
      elements.scrambleLength.value = String(recommendedScrambleLength(N));
      updateAll();
      setStatus("Ready to turn");
    } catch (error) {
      console.error(error);
      setStatus("This cube exceeded the device limit", "error");
    } finally {
      elements.loading.classList.add("done");
    }
  });
}

function scrambleCube() {
  if (animator.isBusy || solveController || sequenceRunning) return;
  stopPlayback();
  clearSolution();
  const seed =
    elements.seed.value.trim() ||
    `RUBIS-${Date.now().toString(36).toUpperCase()}`;
  elements.seed.value = seed;
  const length = Math.max(
    1,
    Math.min(
      500,
      Math.trunc(
        Number(elements.scrambleLength.value) || recommendedScrambleLength(N),
      ),
    ),
  );
  elements.scrambleLength.value = String(length);
  const moves = scrambleSequence(makeRng(seed), N, length, {
    allowWide: N >= 4,
    allowInner: N >= 4,
  });
  const targetState = applyMoves(createSolvedState(N), N, moves);
  const validity = validateState(targetState);
  if (!validity.valid) {
    setStatus(`Invariant failed: ${validity.reason}`, "error");
    return;
  }

  state = createSolvedState(N);
  history = [];
  redoStack = [];
  view.resetPose();
  view.setState(state);
  resetTimer();
  updateAll();
  const duration = Math.max(28, Math.min(100, 1800 / length));
  playMoveSequence(moves, {
    duration,
    record: true,
    onProgress: (completed) =>
      setStatus(`Scrambling ${completed}/${length} · ${seed}`, "busy"),
    onComplete: () => {
      setStatus(`${length}-move scramble · ${seed}`);
      updateAll();
    },
  });
}

function resetCube() {
  cancelMoveSequence();
  if (animator.isBusy) animator.cancel();
  stopPlayback();
  cancelSolve();
  clearSolution();
  state = createSolvedState(N);
  history = [];
  redoStack = [];
  resetTimer();
  view.resetPose();
  view.setState(state);
  updateAll();
  setStatus("Solved state restored");
}

function manualMove(move, { fromAngle = 0 } = {}) {
  if (animator.isBusy || solveController || sequenceRunning) return false;
  stopPlayback();
  clearSolution();
  startTimer();
  return animateMove(move, { record: true, fromAngle });
}

function animateMove(move, context = {}) {
  if (animator.isBusy) return false;
  activeCommit = context;
  const duration = reducedMotion
    ? 0
    : (context.duration ?? 360 / playbackSpeed);
  const started = animator.start(move, {
    fromAngle: context.fromAngle || 0,
    duration,
  });
  if (!started) activeCommit = null;
  return started;
}

function commitAnimatedMove(move) {
  const context = activeCommit || {};
  activeCommit = null;
  applyMove(state, N, move);
  view.setState(state);

  if (context.record) {
    history.push(cloneMove(move));
    redoStack = [];
  }
  if (context.timelineAdvance) {
    const previousCursor = timelineCursor;
    const nextCursor = Math.max(
      0,
      Math.min(timeline.length, previousCursor + context.timelineAdvance),
    );
    if (nextCursor === previousCursor + 1) {
      history.push(cloneMove(timeline.moves[previousCursor]));
    } else if (
      nextCursor === previousCursor - 1 &&
      history.length > timelineBaseHistory.length
    ) {
      history.pop();
    } else {
      history = [
        ...timelineBaseHistory.map(cloneMove),
        ...timeline.moves.slice(0, nextCursor).map(cloneMove),
      ];
    }
    timelineCursor = nextCursor;
    redoStack = [];
  }

  if (timerRunning && isSolved(state)) stopTimer();
  updateAll();
  if (context.after) requestAnimationFrame(context.after);
}

function playMoveSequence(
  moves,
  {
    duration,
    record = false,
    onProgress = () => {},
    onComplete = () => {},
  } = {},
) {
  const generation = ++sequenceGeneration;
  let cursor = 0;
  sequenceRunning = true;
  updateAll();

  const advance = () => {
    if (generation !== sequenceGeneration) return;
    if (cursor >= moves.length) {
      sequenceRunning = false;
      updateAll();
      onComplete();
      return;
    }
    const move = moves[cursor];
    animateMove(move, {
      duration,
      record,
      after: () => {
        cursor++;
        onProgress(cursor, moves.length);
        advance();
      },
    });
  };

  requestAnimationFrame(advance);
}

function cancelMoveSequence() {
  sequenceGeneration++;
  sequenceRunning = false;
  if (animator?.isBusy) {
    animator.cancel();
    activeCommit = null;
  }
}

function undoMove() {
  if (!history.length || animator.isBusy || solveController) return;
  stopPlayback();
  clearSolution();
  const move = history.pop();
  redoStack.push(move);
  animateMove(inverseMove(move), { after: updateAll });
}

function redoMove() {
  if (!redoStack.length || animator.isBusy || solveController) return;
  stopPlayback();
  clearSolution();
  const move = redoStack.pop();
  history.push(move);
  animateMove(move, { after: updateAll });
}

async function solveCube() {
  if (animator.isBusy || solveController || sequenceRunning) return;
  if (isSolved(state)) {
    setStatus("The cube is already solved");
    return;
  }

  stopPlayback();
  clearSolution();
  const generation = ++solveGeneration;
  const controller = new AbortController();
  solveController = controller;
  elements.solve.disabled = true;
  elements.solveProgress.hidden = false;
  setProgress(0.02, "Preparing the solver");
  setStatus("Solving on this device", "busy");

  try {
    solverModulePromise ||= import("./solvers/solver.js");
    const { solveState } = await solverModulePromise;
    if (generation !== solveGeneration) return;
    const result = await solveState({
      N,
      state,
      history: history.map(cloneMove),
      mode: N <= 13 ? "fast" : "trace",
      signal: controller.signal,
      onProgress: ({ value, label }) => setProgress(value, label),
    });
    if (generation !== solveGeneration) return;
    timelineBaseHistory = history.map(cloneMove);
    timeline = new SolutionTimeline(N, state, result.moves);
    timelineCursor = 0;
    renderSolution(result);
    renderTimeline();
    setStatus(`${result.moves.length} moves · independently verified`);
    setProgress(1, "Solution verified");
    setTimeout(() => {
      if (generation === solveGeneration) {
        elements.solveProgress.hidden = true;
        if (timeline?.length) startPlayback({ restartTimer: true });
      }
    }, 280);
  } catch (error) {
    if (error.name !== "AbortError") {
      console.error(error);
      setStatus(error.message, "error");
    } else {
      setStatus("Solve cancelled");
    }
  } finally {
    if (generation === solveGeneration) {
      solveController = null;
      elements.solve.disabled = false;
    }
  }
}

function cancelSolve() {
  solveGeneration++;
  solveController?.abort();
  solveController = null;
  elements.solve.disabled = false;
  elements.solveProgress.hidden = true;
  stopTimer();
}

function renderSolution(result) {
  elements.timelineCard.hidden = false;
  elements.solutionTitle.textContent = result.method;
  elements.solutionDetail.textContent = result.detail;
  elements.solutionMoveCount.textContent = String(result.moves.length);
  elements.solutionTime.textContent = formatElapsed(result.elapsedMs);
  elements.solutionCheck.textContent = result.verified ? "Passed" : "Failed";
}

function clearSolution() {
  stopPlayback();
  timeline = null;
  timelineBaseHistory = [];
  timelineCursor = 0;
  elements.timelineCard.hidden = true;
  resetTimelineDom();
  renderTimeline();
}

function renderTimeline() {
  const enabled = Boolean(timeline?.length);
  elements.range.disabled = !enabled;
  elements.start.disabled = !enabled || timelineCursor === 0;
  elements.prev.disabled = !enabled || timelineCursor === 0;
  elements.play.disabled = !enabled;
  elements.next.disabled = !enabled || timelineCursor === timeline?.length;
  elements.end.disabled = !enabled || timelineCursor === timeline?.length;
  const total = timeline?.length || 0;
  elements.range.max = String(total);
  elements.range.value = String(timelineCursor);
  elements.range.style.setProperty(
    "--range-progress",
    `${total ? (timelineCursor / total) * 100 : 0}%`,
  );
  elements.timelineEnd.textContent = String(total);

  if (!timeline) {
    if (renderedTimeline || elements.moveStrip.childElementCount)
      resetTimelineDom();
    return;
  }

  if (renderedTimeline !== timeline) {
    renderedTimeline = timeline;
    renderedTimelineCursor = timelineCursor;
    timelineWindowStart = -1;
    timelineWindowEnd = -1;
    timelineDomBuilds++;
    scrollTimelineCursor(true);
    renderTimelineWindow(true);
    return;
  }

  if (renderedTimelineCursor === timelineCursor) return;
  const previousCursor = renderedTimelineCursor;
  renderedTimelineCursor = timelineCursor;
  scrollTimelineCursor(false);
  renderTimelineWindow(false, [previousCursor, timelineCursor]);
}

function resetTimelineDom() {
  renderedTimeline = null;
  renderedTimelineCursor = -1;
  timelineWindowStart = -1;
  timelineWindowEnd = -1;
  timelineWindowColumns = 1;
  timelineWindowForceRender = false;
  if (timelineWindowFrame) cancelAnimationFrame(timelineWindowFrame);
  timelineWindowFrame = 0;
  elements.moveStripSpacer.style.height = "0px";
  elements.moveStripWindow.style.transform = "translateY(0)";
  elements.moveStripWindow.replaceChildren();
}

function scrollTimelineCursor(force) {
  if (!timeline?.length) return;
  const { columns } = updateTimelineVirtualSpace();
  const targetIndex = Math.min(timelineCursor, timeline.length - 1);
  const row = Math.floor(targetIndex / columns);
  const top = row * MOVE_ROW_HEIGHT;
  const bottom =
    MOVE_LIST_TOP_SPACE +
    top +
    MOVE_ROW_HEIGHT -
    MOVE_GRID_GAP +
    MOVE_LIST_BOTTOM_SPACE;
  const viewportTop = elements.moveStrip.scrollTop;
  const viewportBottom = viewportTop + elements.moveStrip.clientHeight;
  let nextScrollTop = viewportTop;
  if (top < viewportTop) nextScrollTop = top;
  else if (bottom > viewportBottom)
    nextScrollTop = bottom - elements.moveStrip.clientHeight;
  if (force && targetIndex === 0) nextScrollTop = 0;
  if (nextScrollTop !== viewportTop)
    elements.moveStrip.scrollTop = Math.max(0, nextScrollTop);
}

function updateTimelineVirtualSpace() {
  const width = Math.max(
    1,
    elements.moveStripSpacer.clientWidth || elements.moveStrip.clientWidth,
  );
  const columns = Math.max(
    1,
    Math.floor(
      (width + MOVE_GRID_GAP) / (MOVE_BUTTON_MIN_WIDTH + MOVE_GRID_GAP),
    ),
  );
  const rows = Math.ceil((timeline?.length || 0) / columns);
  const height = Math.max(
    0,
    rows * MOVE_ROW_HEIGHT - MOVE_GRID_GAP + MOVE_LIST_BOTTOM_SPACE,
  );
  timelineWindowColumns = columns;
  elements.moveStripSpacer.style.height = `${height}px`;
  elements.moveStripWindow.style.setProperty("--move-columns", String(columns));
  return { columns, rows };
}

function renderTimelineWindow(force = false, changedCursors = []) {
  if (!timeline) return;
  const { columns, rows } = updateTimelineVirtualSpace();
  const firstVisibleRow = Math.max(
    0,
    Math.floor(elements.moveStrip.scrollTop / MOVE_ROW_HEIGHT),
  );
  const visibleRows = Math.max(
    1,
    Math.ceil(elements.moveStrip.clientHeight / MOVE_ROW_HEIGHT),
  );
  const startRow = Math.max(0, firstVisibleRow - MOVE_WINDOW_BUFFER_ROWS);
  const endRow = Math.min(
    rows,
    firstVisibleRow + visibleRows + MOVE_WINDOW_BUFFER_ROWS,
  );
  const start = startRow * columns;
  const end = Math.min(timeline.length, endRow * columns);

  if (
    !force &&
    start === timelineWindowStart &&
    end === timelineWindowEnd &&
    columns === timelineWindowColumns
  ) {
    for (const cursor of changedCursors) updateVisibleTimelineButton(cursor);
    return;
  }

  timelineWindowStart = start;
  timelineWindowEnd = end;
  elements.moveStripWindow.style.transform = `translateY(${startRow * MOVE_ROW_HEIGHT}px)`;
  elements.moveStripWindow.innerHTML = timeline.moves
    .slice(start, end)
    .map((move, offset) => {
      const index = start + offset;
      const notation = formatMove(move, N);
      return `<button type="button" data-cursor="${index}" class="${index < timelineCursor ? "done" : index === timelineCursor ? "active" : ""}" aria-label="Jump to move ${index + 1}: ${escapeHtml(notation)}">${escapeHtml(notation)}</button>`;
    })
    .join("");
  timelineWindowRenders++;
}

function updateVisibleTimelineButton(index) {
  if (index < timelineWindowStart || index >= timelineWindowEnd) return;
  const button = elements.moveStripWindow.children[index - timelineWindowStart];
  if (!button) return;
  button.classList.toggle("done", index < timelineCursor);
  button.classList.toggle("active", index === timelineCursor);
}

function scheduleTimelineWindowRender(force = false) {
  timelineWindowForceRender ||= force;
  if (timelineWindowFrame) return;
  timelineWindowFrame = requestAnimationFrame(() => {
    timelineWindowFrame = 0;
    const shouldForce = timelineWindowForceRender;
    timelineWindowForceRender = false;
    renderTimelineWindow(shouldForce);
  });
}

function setTimelineCursor(rawCursor, { preservePlayback = false } = {}) {
  if (!timeline) return;
  const shouldResume = preservePlayback && playing;
  stopPlayback();
  if (animator.isBusy) {
    animator.cancel();
    activeCommit = null;
  }
  timelineCursor = Math.max(
    0,
    Math.min(timeline.length, Math.trunc(rawCursor)),
  );
  state = timeline.stateAt(timelineCursor);
  history = [
    ...timelineBaseHistory.map(cloneMove),
    ...timeline.moves.slice(0, timelineCursor).map(cloneMove),
  ];
  redoStack = [];
  view.resetPose();
  view.setState(state);
  updateAll();
  if (shouldResume && timelineCursor < timeline.length) startPlayback();
}

function nextTimelineMove(fromPlayback, generation = playbackGeneration) {
  if (!fromPlayback) stopPlayback();
  if (fromPlayback && (!playing || generation !== playbackGeneration)) return;
  if (!timeline || animator.isBusy) return;
  if (timelineCursor >= timeline.length) {
    if (fromPlayback && elements.loop.checked) {
      setTimelineCursor(0);
      resetTimer();
      startTimer();
      playing = true;
      const nextGeneration = ++playbackGeneration;
      updatePlayButton();
      requestAnimationFrame(() => nextTimelineMove(true, nextGeneration));
    } else {
      stopPlayback();
    }
    return;
  }
  const move = timeline.moves[timelineCursor];
  animateMove(move, {
    timelineAdvance: 1,
    after: fromPlayback ? () => nextTimelineMove(true, generation) : null,
  });
}

function previousTimelineMove() {
  if (!timeline || timelineCursor <= 0 || animator.isBusy) return;
  stopPlayback();
  const move = inverseMove(timeline.moves[timelineCursor - 1]);
  animateMove(move, { timelineAdvance: -1 });
}

function togglePlayback() {
  if (!timeline) return;
  if (playing) stopPlayback();
  else startPlayback();
}

function startPlayback({ restartTimer = false } = {}) {
  if (!timeline || animator.isBusy) return;
  if (timelineCursor >= timeline.length) {
    setTimelineCursor(0);
    restartTimer = true;
  }
  if (restartTimer || timelineCursor === 0) resetTimer();
  startTimer();
  playing = true;
  const generation = ++playbackGeneration;
  updatePlayButton();
  nextTimelineMove(true, generation);
}

function stopPlayback() {
  playbackGeneration++;
  if (playing) stopTimer();
  playing = false;
  updatePlayButton();
}

function updatePlayButton() {
  elements.play
    .querySelector("use")
    ?.setAttribute("href", playing ? "#icon-pause" : "#icon-play");
  elements.play.setAttribute(
    "aria-label",
    playing ? "Pause solution" : "Play solution",
  );
}

function selectPlaybackSpeed(speed) {
  const numericSpeed = Number(speed);
  playbackSpeed = Number.isFinite(numericSpeed)
    ? Math.min(
        MAX_PLAYBACK_SPEED,
        Math.max(MIN_PLAYBACK_SPEED, Math.round(numericSpeed * 2) / 2),
      )
    : 1;
  elements.speedValue.textContent = `${Number.isInteger(playbackSpeed) ? playbackSpeed : playbackSpeed.toFixed(1)}×`;
  elements.speedDown.disabled = playbackSpeed <= MIN_PLAYBACK_SPEED;
  elements.speedUp.disabled = playbackSpeed >= MAX_PLAYBACK_SPEED;
  writePreference("speed", String(playbackSpeed));
}

function stepPlaybackSpeed(direction) {
  selectPlaybackSpeed(playbackSpeed + Math.sign(direction || 1));
}

function updateAll() {
  elements.cubeName.textContent = `${N}×${N}`;
  elements.cubeSize.value = String(N);
  syncCubeSizeInputWidth();
  elements.moveCount.textContent = String(history.length);
  elements.undo.disabled =
    history.length === 0 || animator?.isBusy || sequenceRunning;
  elements.redo.disabled =
    redoStack.length === 0 || animator?.isBusy || sequenceRunning;
  elements.scramble.disabled = sequenceRunning || Boolean(solveController);
  elements.solve.disabled =
    sequenceRunning || Boolean(solveController) || isSolved(state);
  elements.instanceCount.textContent = `${(6 * N * N).toLocaleString()} facelets`;
  updateSolverCapability();
  renderTimeline();
}

function syncCubeSizeInputWidth() {
  const digits = elements.cubeSize.value.replace(/\D/g, "").length || 1;
  elements.cubeSize.style.setProperty(
    "--size-digits",
    String(Math.min(digits, 4)),
  );
  elements.cubeSizeDown.disabled = Number(elements.cubeSize.value) <= 2;
}

function updateSolverCapability() {
  const searchable = N <= 13;
  elements.solverCapability.textContent = searchable
    ? N <= 3
      ? "Rubis will independently search this state, verify the result, then animate it."
      : `Rubis will independently reduce this ${N}×${N} state, solve its 3×3 core, and verify the full replay.`
    : `${N}×${N} will use exact recorded-trace solving; arbitrary-state reduction is not implemented.`;
  elements.solverCapability.classList.toggle("limited", !searchable);
}

function setStatus(message, kind = "") {
  elements.statusText.textContent = message;
  elements.status.classList.toggle("busy", kind === "busy");
  elements.status.classList.toggle("error", kind === "error");
}

function setProgress(value, label) {
  const safe = Math.max(0, Math.min(1, value));
  elements.progressBar.value = safe;
  elements.progressValue.textContent = `${Math.round(safe * 100)}%`;
  elements.progressLabel.textContent = label;
}

function startTimer() {
  if (timerRunning || isSolved(state)) return;
  timerRunning = true;
  timerStartedAt = performance.now();
}

function stopTimer() {
  if (!timerRunning) return;
  timerElapsed += performance.now() - timerStartedAt;
  timerRunning = false;
}

function resetTimer() {
  timerRunning = false;
  timerStartedAt = 0;
  timerElapsed = 0;
  setTimerText("00:00.0");
}

function updateTimer(now) {
  const elapsed = timerElapsed + (timerRunning ? now - timerStartedAt : 0);
  const minutes = Math.floor(elapsed / 60_000);
  const seconds = Math.floor((elapsed % 60_000) / 1000);
  const tenths = Math.floor((elapsed % 1000) / 100);
  setTimerText(
    `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${tenths}`,
  );
}

function setTimerText(value) {
  if (value === timerText) return;
  timerText = value;
  elements.timer.textContent = value;
  timerDomWrites++;
}

function updateDiagnostics() {
  if (elements.performanceHud.hidden || !view) return;
  elements.debugFps.textContent = currentFps ? currentFps.toFixed(0) : "-";
  elements.debugFrame.textContent = currentFrameMs
    ? `${currentFrameMs.toFixed(1)} ms`
    : "-";
  elements.debugCubies.textContent =
    view.cubiePositions.length.toLocaleString();
  elements.debugFacelets.textContent = (6 * N * N).toLocaleString();
  elements.debugDraws.textContent = String(view.renderer.info.render.calls);
  elements.debugMemory.textContent = formatBytes(
    6 * N * N * Uint32Array.BYTES_PER_ELEMENT,
  );
}

function recommendedScrambleLength(size) {
  if (size === 2) return 11;
  if (size === 3) return 20;
  return Math.min(80, 20 + size * 2);
}

function surfaceCubieCount(size) {
  return size <= 2 ? size ** 3 : 6 * size * size - 12 * size + 8;
}

function formatElapsed(ms) {
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`;
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function readPreference(key, fallback) {
  try {
    return localStorage.getItem(`rubis:${key}`) || fallback;
  } catch {
    return fallback;
  }
}

function writePreference(key, value) {
  try {
    localStorage.setItem(`rubis:${key}`, value);
  } catch {
    /* Preferences are optional. */
  }
}

function escapeHtml(value) {
  return String(value).replace(
    /[&<>'"]/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "'": "&#39;",
        '"': "&quot;",
      })[character],
  );
}

// Small, read-only test surface for browser smoke tests and local diagnostics.
window.__rubis = {
  getSnapshot: () => ({
    N,
    solved: isSolved(state),
    historyLength: history.length,
    timelineCursor,
    timelineLength: timeline?.length || 0,
    playing,
    playbackSpeed,
    playbackGeneration,
    timelineDomBuilds,
    timelineWindowRenders,
    sequenceRunning,
    cameraRadius: cameraRig.radius,
    cameraTheta: cameraRig.theta,
    cameraPhi: cameraRig.phi,
    cameraPosition: view.camera.position.toArray(),
    rearCameraPosition: view.rearCamera.position.toArray(),
    performanceOpen: !elements.performanceHud.hidden,
    timer: elements.timer.textContent,
    timerDomWrites,
    renderedMoveButtons: elements.moveStripWindow.childElementCount,
  }),
  formatHistory: () => formatSequence(history, N),
};
